import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, Phone, CreditCard, Loader2, CheckCircle, Sparkles, Calendar, Shirt, ShoppingCart, Plus, Monitor, Globe, Video, Award, Clock, Package, Percent, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const virtualCourses = [
  { id: 'cpr-aed', name: 'Heartsaver CPR/AED', price: 85, duration: '2.5-3 Hours', description: 'Learn adult, child & infant CPR, AED use, and relief of choking.' },
  { id: 'first-aid-cpr', name: 'Heartsaver First Aid CPR/AED', price: 105, duration: '3.5-4 Hours', description: 'Learn essential first aid, plus CPR & AED skills.' },
  { id: 'combo', name: 'CPR + First Aid Combo', price: 175, duration: '4.5-5 Hours', description: 'Get both courses and save! Best value.', popular: true },
];

const whoShouldTake = [
  'Nurses', 'CNAs', 'Medical Assistants', 'Home Health Aides', 'PCAs', 'EMT Students',
  'Teachers', 'Coaches', 'Childcare Providers', 'Babysitters', 'Foster & Adoptive Parents',
  'Churches & Ministry Teams', 'Workplace Safety Teams'
];

const usStates = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];

export default function VirtualCPR() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', state: '', course: '',
    isTravelNurse: false, apply4thJuly: false, notes: ''
  });
  const [success, setSuccess] = useState(false);

  const selectedCourse = virtualCourses.find(c => c.id === formData.course);
  let basePrice = selectedCourse?.price || 0;
  let total = basePrice;
  let discountAmount = 0;
  if (formData.isTravelNurse && basePrice > 0) { discountAmount += basePrice * 0.10; }
  if (formData.apply4thJuly) { discountAmount += 5; }
  total = Math.max(0, total - discountAmount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.course) { toast.error('Please select a course'); return; }
    if (!formData.state) { toast.error('Please select your state'); return; }
    setIsSubmitting(true);
    
    const discounts = [];
    if (formData.isTravelNurse) discounts.push('10% Travel Nurse/CNA Discount');
    if (formData.apply4thJuly) discounts.push('4th of July Special ($5 OFF)');

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        subject: `Virtual CPR Registration - ${selectedCourse?.name} - ${formData.state}`,
        message: `VIRTUAL CPR & FIRST AID REGISTRATION

Course: ${selectedCourse?.name}
Base Price: $${basePrice}
${discounts.length > 0 ? `Discounts Applied: ${discounts.join(', ')}` : ''}
Discount Amount: -$${discountAmount.toFixed(2)}
TOTAL: $${total.toFixed(2)}

STUDENT INFORMATION:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
State: ${formData.state}

Notes: ${formData.notes || 'None'}

HOW IT WORKS:
1. Complete online AHA coursework
2. Use your own approved manikin or rent one ($10/day)
3. Schedule live Zoom skills session
4. Receive AHA eCard upon completion`
      });

      const response = await axios.post(`${API}/payments/create-link`, {
        booking_id: `virtual-${Date.now()}`, booking_type: 'virtual-cpr', amount: Math.round(total * 100) / 100,
        description: `Virtual ${selectedCourse?.name}`,
        customer_name: formData.name, customer_email: formData.email
      });
      if (response.data?.payment_link_url) { toast.success('Redirecting to payment...'); window.open(response.data.payment_link_url, '_blank'); }
      setSuccess(true);
    } catch (error) { toast.error('Could not process registration. Please call 912-388-6305'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Globe className="w-10 h-10" />
              <Badge className="bg-white/20 text-white text-lg px-4 py-2">SERVING ALL 50 STATES</Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Virtual CPR & First Aid Certification
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-6">
              Learn. Practice. Get Certified — From Anywhere in the USA!
            </p>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              Live, instructor-led American Heart Association Heartsaver CPR, AED, and First Aid training via Zoom
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Badge className="bg-yellow-400 text-yellow-900 text-base px-4 py-2"><Clock className="w-4 h-4 mr-2 inline" /> 24-Hour Turnaround</Badge>
              <Badge className="bg-pink-500 text-white text-base px-4 py-2"><Percent className="w-4 h-4 mr-2 inline" /> 10% OFF Travel Nurses & CNAs</Badge>
              <Badge className="bg-red-500 text-white text-base px-4 py-2"><Flag className="w-4 h-4 mr-2 inline" /> 4th of July Special: $5 OFF</Badge>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            
            {/* Left Column - Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-8">
              
              {/* How It Works */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-cyan-200">
                <h2 className="text-2xl font-bold text-cyan-800 mb-6 flex items-center gap-3">
                  <Video className="w-7 h-7 text-cyan-600" /> How It Works
                </h2>
                <div className="space-y-6">
                  {[
                    { step: 1, title: 'Register Online', desc: 'Choose your course and complete your registration.' },
                    { step: 2, title: 'Complete the Online Learning', desc: 'Finish the required American Heart Association online coursework at your own pace.' },
                    { step: 3, title: 'Training Equipment', desc: 'Use your own approved CPR manikin or rent one from us for $10/day.' },
                    { step: 4, title: 'Live Zoom Skills Session', desc: 'Meet one-on-one or in a small group with a certified instructor to complete your hands-on skills evaluation.' },
                    { step: 5, title: 'Receive Your eCard', desc: 'After successfully completing all course components, your American Heart Association eCard will be issued.' },
                  ].map(item => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg">
                        {item.step}
                      </div>
                      <div>
                        <p className="font-bold text-cyan-800 text-lg">{item.title}</p>
                        <p className="text-cyan-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-200">
                  <p className="text-orange-800"><strong>Need a manikin?</strong> Rent one from us for just <strong>$10/day</strong>! Contact us to arrange your rental.</p>
                </div>
              </div>

              {/* Course Options */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-200">
                <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center gap-3">
                  <Award className="w-7 h-7 text-blue-600" /> Course Options
                </h2>
                <div className="space-y-4">
                  {virtualCourses.map(course => (
                    <div key={course.id} className={`p-5 rounded-xl border-2 ${course.popular ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-blue-800 text-lg">{course.name}</p>
                          <p className="text-blue-600">{course.description}</p>
                          <p className="text-sm text-gray-500 mt-1"><Clock className="w-4 h-4 inline mr-1" />{course.duration}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-black text-blue-600">${course.price}</p>
                          {course.popular && <Badge className="bg-blue-500 text-white">BEST VALUE</Badge>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="p-5 rounded-xl border-2 border-orange-300 bg-orange-50">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-orange-800 text-lg"><Package className="w-5 h-5 inline mr-2" />Manikin Rental</p>
                        <p className="text-orange-600">Need equipment? Rent an approved CPR manikin from us!</p>
                      </div>
                      <p className="text-3xl font-black text-orange-600">$10<span className="text-base font-normal">/day</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Who Should Take */}
              <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-indigo-200">
                <h2 className="text-2xl font-bold text-indigo-800 mb-6 flex items-center gap-3">
                  <Users className="w-7 h-7 text-indigo-600" /> Who Should Take This Course?
                </h2>
                <div className="flex flex-wrap gap-2">
                  {whoShouldTake.map(who => (
                    <Badge key={who} variant="outline" className="border-indigo-300 text-indigo-700 text-sm py-1 px-3">{who}</Badge>
                  ))}
                  <Badge className="bg-indigo-500 text-white text-sm py-1 px-3">Anyone who wants to save lives!</Badge>
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl p-8 text-white shadow-xl">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Sparkles className="w-7 h-7" /> Why Choose LifeBreath CPR Academy?
                </h2>
                <ul className="grid md:grid-cols-2 gap-3">
                  {[
                    'Certified, experienced instructor',
                    'Flexible scheduling',
                    'Live Zoom instruction',
                    'Nationwide service',
                    'Fast, friendly, professional training',
                    'Individual and group classes',
                    'Same-day support',
                    'Excellent customer service'
                  ].map(item => (
                    <li key={item} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-cyan-300 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xl font-bold text-center text-cyan-200">Because Every Breath Matters.</p>
              </div>
            </motion.div>

            {/* Right Column - Registration Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="lg:sticky lg:top-8">
              {success ? (
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 shadow-2xl">
                  <CardContent className="p-10 text-center">
                    <CheckCircle className="w-24 h-24 text-green-500 mx-auto mb-6" />
                    <h2 className="text-3xl font-bold text-green-800 mb-3">Registration Complete!</h2>
                    <p className="text-green-700 text-lg mb-6">Check your email for course access instructions.</p>
                    <Button onClick={() => setSuccess(false)} className="bg-green-500 hover:bg-green-600 text-lg px-8 py-3">
                      Register Another Student
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-white border-cyan-300 border-2 overflow-hidden shadow-2xl">
                  <CardHeader className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-6">
                    <CardTitle className="text-2xl flex items-center gap-3">
                      <Monitor className="w-7 h-7" /> Register Now
                    </CardTitle>
                    <CardDescription className="text-white/90 text-lg">
                      Live Zoom Training • AHA Certification
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Course Selection */}
                      <div>
                        <Label className="font-bold text-blue-800 text-base">Select Course *</Label>
                        <Select value={formData.course} onValueChange={v => setFormData({...formData, course: v})}>
                          <SelectTrigger className="mt-2 border-blue-200 h-12 text-base" data-testid="virtual-course-select">
                            <SelectValue placeholder="Choose a course" />
                          </SelectTrigger>
                          <SelectContent>
                            {virtualCourses.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name} - ${c.price}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Manikin Rental Info */}
                      <div className="p-4 rounded-xl border-2 border-orange-200 bg-orange-50">
                        <div className="flex items-start gap-3">
                          <Package className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                          <div>
                            <p className="font-bold text-orange-800">Need a Manikin? Rent for $10/day</p>
                            <p className="text-sm text-orange-600">Contact us after registration to arrange your rental. Mention it in the notes below!</p>
                          </div>
                        </div>
                      </div>

                      {/* Discounts */}
                      <div className="bg-gradient-to-r from-pink-50 to-red-50 p-4 rounded-xl border border-pink-200">
                        <p className="font-bold text-pink-800 mb-3"><Percent className="w-5 h-5 inline mr-2" />Available Discounts</p>
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox checked={formData.isTravelNurse} onCheckedChange={v => setFormData({...formData, isTravelNurse: v})} className="border-pink-400 w-5 h-5" />
                            <span className="text-pink-700"><strong>10% OFF</strong> Travel Nurses & CNAs (valid ID required)</span>
                          </label>
                          <label className="flex items-center gap-3 cursor-pointer">
                            <Checkbox checked={formData.apply4thJuly} onCheckedChange={v => setFormData({...formData, apply4thJuly: v})} className="border-red-400 w-5 h-5" />
                            <span className="text-red-700"><Flag className="w-4 h-4 inline mr-1" /><strong>4th of July Special</strong> - Extra $5 OFF</span>
                          </label>
                        </div>
                      </div>

                      {/* Student Info */}
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <h4 className="font-bold text-slate-800 mb-4 text-base"><Users className="w-5 h-5 inline mr-2" />Student Information</h4>
                        <div className="space-y-4">
                          <div>
                            <Label>Full Name *</Label>
                            <Input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Your full name" className="mt-1 h-11" />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>Email *</Label>
                              <Input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="mt-1 h-11" />
                            </div>
                            <div>
                              <Label>Phone *</Label>
                              <Input type="tel" required value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="Phone" className="mt-1 h-11" />
                            </div>
                          </div>
                          <div>
                            <Label>Your State *</Label>
                            <Select value={formData.state} onValueChange={v => setFormData({...formData, state: v})}>
                              <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select your state" /></SelectTrigger>
                              <SelectContent>{usStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Notes (Optional)</Label>
                            <Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Any questions or special requests" className="mt-1" rows={2} />
                          </div>
                        </div>
                      </div>

                      {/* Price Summary */}
                      <div className="bg-gradient-to-r from-cyan-100 to-blue-100 rounded-xl p-5 border-2 border-cyan-300">
                        <div className="space-y-2 text-base">
                          {selectedCourse && (
                            <div className="flex justify-between"><span className="text-cyan-700">{selectedCourse.name}</span><span className="font-semibold">${basePrice}</span></div>
                          )}
                          {discountAmount > 0 && (
                            <div className="flex justify-between text-green-600"><span>Discounts Applied</span><span className="font-semibold">-${discountAmount.toFixed(2)}</span></div>
                          )}
                        </div>
                        <div className="border-t border-cyan-300 mt-4 pt-4 text-center">
                          <p className="text-cyan-800 text-sm">Total Due</p>
                          <p className="text-5xl font-black text-cyan-900">${total.toFixed(2)}</p>
                        </div>
                      </div>

                      {/* Merch Upsell */}
                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border-2 border-yellow-300">
                        <div className="flex items-center gap-2 mb-3">
                          <Shirt className="w-5 h-5 text-[#c31c31]" />
                          <h4 className="font-bold text-[#2a2873]">Get LifeBreath Merch!</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                            <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/cu7r1n8c_ChatGPT%20Image%20Mar%206%2C%202026%2C%2011_26_09%20AM.png" alt="T-Shirts" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                            <p className="font-bold text-[#c31c31] text-sm">T-Shirts</p>
                            <p className="text-xs text-slate-500">$20-$30</p>
                          </div>
                          <div className="bg-white rounded-lg p-2 text-center shadow-sm">
                            <img src="https://customer-assets.emergentagent.com/job_3768ece8-ea61-4509-ba44-1a2ad698e204/artifacts/675b2fub_1CF43A2B-731C-43A8-97B9-E288CF0EAA32%20%281%29.PNG" alt="Cups" className="w-14 h-14 object-cover rounded-lg mx-auto mb-1" />
                            <p className="font-bold text-orange-500 text-sm">Cups</p>
                            <p className="text-xs text-slate-500">$25</p>
                          </div>
                        </div>
                        <Link to="/shop">
                          <Button variant="outline" className="w-full rounded-full border-2 border-[#c31c31] text-[#c31c31] hover:bg-[#c31c31] hover:text-white">
                            <ShoppingCart className="w-4 h-4 mr-2" /> Shop Merch
                          </Button>
                        </Link>
                      </div>

                      <Button 
                        type="submit" 
                        disabled={isSubmitting || !formData.course} 
                        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 rounded-full py-7 text-xl font-bold shadow-xl"
                      >
                        {isSubmitting ? (
                          <><Loader2 className="w-6 h-6 mr-2 animate-spin" />Processing...</>
                        ) : (
                          <><CreditCard className="w-6 h-6 mr-2" />Register & Pay ${total.toFixed(2)}</>
                        )}
                      </Button>
                      <p className="text-center text-sm text-cyan-600">Secure payment powered by Square</p>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Contact */}
              <div className="mt-8 text-center bg-white rounded-xl p-6 shadow-lg border border-cyan-200">
                <p className="text-cyan-600 mb-2">Questions? Call or Text:</p>
                <a href="tel:912-388-6305" className="inline-flex items-center gap-2 text-cyan-800 font-bold text-2xl hover:text-cyan-600 transition-colors">
                  <Phone className="w-6 h-6" />
                  912-388-6305
                </a>
                <p className="text-cyan-600 mt-4 text-lg font-semibold">LifeBreath CPR Academy</p>
                <p className="text-cyan-500 italic">Because Every Breath Matters.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
